import { useEffect, useRef, useState } from 'react';
import { Alert } from '../../shared/ui/components';
import { canPickMonitor, findMonitor, inDesktopApp, isProjecting, watchOutputs, listMonitors, loadChoice, monitorsAllowed, project, saveChoice, stopProjecting, type Monitor, type MonitorChoice, type Output } from './screens';

const OUTPUTS: { output: Output; label: string; button: string }[] = [
  { output: 'congregacion', label: 'Congregación (proyector)', button: 'Abrir proyector' },
  { output: 'escenario', label: 'Músicos (escenario)', button: 'Escenario' },
];

/** Monitores de esta computadora y en cuál va cada pantalla (se guarda en esta computadora). */
function useMonitors() {
  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [choice, setChoice] = useState<MonitorChoice>(loadChoice);
  const [error, setError] = useState<string | null>(null);
  const supported = canPickMonitor();

  // Si ya dio permiso antes (en la app KEORA Multimedia siempre), los monitores se leen sin preguntar.
  useEffect(() => {
    if (!supported) return;
    void (inDesktopApp() ? Promise.resolve(true) : monitorsAllowed())
      .then((ok) => (ok ? listMonitors().then(setMonitors) : undefined))
      .catch(() => undefined);
  }, [supported]);

  const monitorFor = (output: Output) => findMonitor(choice[output], monitors);

  // Lo guardado se pone al día con el monitor que se reconoció (o se pasa del formato viejo).
  useEffect(() => {
    if (monitors.length === 0) return;
    let changed = false;
    const next: MonitorChoice = { ...choice };
    for (const o of ['congregacion', 'escenario'] as Output[]) {
      const found = findMonitor(choice[o], monitors);
      const saved = choice[o];
      if (found && (typeof saved === 'string' || saved?.key !== found.key || saved.name !== found.name)) {
        next[o] = found;
        changed = true;
      }
    }
    if (changed) {
      saveChoice(next);
      setChoice(next);
    }
  }, [monitors, choice]);

  function choose(output: Output, key: string | null) {
    setChoice((prev) => {
      const next = { ...prev, [output]: (key && monitors.find((m) => m.key === key)) || undefined };
      saveChoice(next);
      return next;
    });
  }

  async function detect() {
    setError(null);
    try {
      const list = await listMonitors();
      setMonitors(list);
      // Primera vez con dos monitores: el que no es el principal suele ser el proyector.
      if (!choice.congregacion && list.length > 1) choose('congregacion', list.find((m) => !m.primary)?.key ?? null);
    } catch {
      setError('No se dio permiso para ver los monitores. Puedes darlo en el candado junto a la dirección del sitio.');
    }
  }

  return { supported, monitors, monitorFor, choose, detect, error, setError };
}

type Monitors = ReturnType<typeof useMonitors>;

function MonitorPanel({ m }: { m: Monitors }) {
  if (!m.supported) return <p className="small">Para elegir el monitor de cada pantalla usa Chrome o Edge en la computadora de proyección. Aquí las pantallas se abren en una pestaña nueva.</p>;
  return (
    <>
      <p className="small muted">Elige una vez en qué monitor va cada pantalla. Se guarda en esta computadora y desde entonces «Abrir proyector» la abre ahí, a pantalla completa. La de los músicos se abre sola al entrar a la consola.</p>
      {m.monitors.length === 0 ? (
        <button type="button" className="btn btn--secondary" onClick={() => void m.detect()}>
          <span>Buscar monitores</span>
        </button>
      ) : (
        <div className="form monitors__form">
          {OUTPUTS.map((o) => (
            <label key={o.output}>
              {o.label}
              <select value={m.monitorFor(o.output)?.key ?? ''} onChange={(e) => m.choose(o.output, e.target.value || null)}>
                <option value="">Pestaña nueva (sin monitor fijo)</option>
                {m.monitors.map((mon) => (
                  <option key={mon.key} value={mon.key}>
                    {mon.name}
                  </option>
                ))}
              </select>
            </label>
          ))}
          {m.monitors.length < 2 ? <p className="small muted">Solo se ve un monitor. Conecta el proyector, elige «Extender» y toca «Buscar de nuevo».</p> : null}
          <button type="button" className="link-button" onClick={() => void m.detect()}>
            Buscar de nuevo
          </button>
        </div>
      )}
    </>
  );
}

/** Tarjeta de monitores para la Configuración de la app KEORA Multimedia (el engrane del menú). */
export function MonitorsCard() {
  const m = useMonitors();
  return (
    <div className="card monitors" role="region" aria-label="Monitores">
      <h2>Monitores</h2>
      {m.error ? <Alert>{m.error}</Alert> : null}
      <MonitorPanel m={m} />
    </div>
  );
}

/**
 * Botones de la consola para mandar cada pantalla a su monitor. El monitor de cada pantalla se
 * elige una vez en Configuración y se guarda en esta computadora; después basta un clic y la
 * pantalla se abre en su monitor a pantalla completa.
 */
export function ProjectButtons({ churchId, presentationId }: { churchId: string; presentationId: string }) {
  const m = useMonitors();
  const { monitorFor, error, setError } = m;
  // Chrome gastó el clic al abrir la ventana: falta un clic más para la pantalla completa.
  const [pending, setPending] = useState<Output | null>(null);
  // En la app, la pantalla de los músicos se abre sola en su monitor al entrar a la consola.
  const stageMonitor = monitorFor('escenario');
  const autoStage = inDesktopApp() && stageMonitor !== null;
  const stageOpened = useRef(false);
  useEffect(() => {
    if (!autoStage || stageOpened.current) return;
    stageOpened.current = true;
    go('escenario');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStage]);

  const ids = { churchId, presentationId };
  // «Abrir proyector» es un interruptor: se enciende al abrir la pantalla de los asistentes y se apaga al cerrarla.
  // Tras recargar la consola, la pantalla que sigue abierta avisa que está ahí y «Abrir proyector» se queda encendido.
  const [live, setLive] = useState(false);
  useEffect(() => {
    const stop = watchOutputs(ids);
    const t = setInterval(() => setLive(isProjecting(ids, 'congregacion')), 1000);
    return () => {
      clearInterval(t);
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [churchId, presentationId]);

  function go(output: Output) {
    setError(null);
    setPending(null);
    const url = `/app/pantalla/${churchId}/${presentationId}/${output}`;
    if (!project(url, ids, output, monitorFor(output), () => setPending(output))) {
      setError('El navegador bloqueó la ventana. Permite las ventanas emergentes de este sitio y vuelve a intentarlo.');
      return;
    }
    if (output === 'congregacion') setLive(true);
  }

  function toggle(output: Output) {
    if (output === 'congregacion' && live && pending !== 'congregacion') {
      stopProjecting(ids, output);
      setLive(false);
      return;
    }
    go(output);
  }

  return (
    <>
      {[...OUTPUTS]
        .reverse()
        .filter((o) => !(o.output === 'escenario' && autoStage))
        .map((o) => {
          const isLive = o.output === 'congregacion';
          const where = monitorFor(o.output) ? `Se abre en: ${monitorFor(o.output)?.name}` : 'Se abre en una pestaña nueva';
          return (
            <button
              key={o.output}
              type="button"
              className={`btn ${isLive ? `ewc__live${live ? ' ewc__live--on' : ''}` : 'btn--secondary'}`}
              aria-pressed={isLive ? live : undefined}
              onClick={() => toggle(o.output)}
              title={isLive && live ? 'Clic para dejar de proyectar' : where}
            >
              <span>{o.button}</span>
            </button>
          );
        })}
      {pending ? (
        <div className="card monitors" role="status">
          <p className="small">La pantalla ya está en su monitor. Toca este botón para ponerla en pantalla completa (solo pasa la primera vez que se abre).</p>
          <button type="button" className="btn btn--primary" onClick={() => go(pending)}>
            <span>Poner en pantalla completa</span>
          </button>
        </div>
      ) : null}
      {error ? (
        <div className="card monitors" role="region" aria-label="Monitores">
          <Alert>{error}</Alert>
        </div>
      ) : null}
    </>
  );
}
